import { effect, inject, Injectable, OnDestroy } from '@angular/core'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { DialogConfirmComponent } from '@components/dialogs/dialog-confirm/dialog-confirm.component'
import { loggedOutRedirectUri, reloginRedirectUri } from '@core/auth/auth-redirect'
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular'
import Keycloak from 'keycloak-js'

/** Renew the access token once it has less than this many seconds of life left. */
const TOKEN_MIN_VALIDITY_SECONDS = 70
/** How often the token is checked while the tab is open. */
const REFRESH_INTERVAL_MS = 60_000

/**
 * Keeps the Keycloak access token fresh in the background for as long as the tab
 * is open. `updateToken()` is a plain request to Keycloak's token endpoint - no
 * redirect, no page reload - so an active user never notices.
 *
 * When the token can no longer be refreshed silently (refresh token expired,
 * SSO session ended elsewhere, ...) the user is asked to re-authenticate rather
 * than being dropped on Keycloak's "you are logged out" page.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService implements OnDestroy {
  private readonly _keycloak = inject(Keycloak)
  private readonly _dialog = inject(MatDialog)
  private readonly _keycloakEvent = inject(KEYCLOAK_EVENT_SIGNAL)

  private _intervalId?: ReturnType<typeof setInterval>
  private _reauthDialog?: MatDialogRef<DialogConfirmComponent>
  private _refreshing = false

  constructor() {
    effect(() => {
      const event = this._keycloakEvent()
      if (!event) return

      switch (event.type) {
        case KeycloakEventType.TokenExpired:
          // The token lapsed between two checks - renew it right away.
          void this._refresh()
          break
        case KeycloakEventType.AuthRefreshError:
          // Keycloak's own refresh attempt failed - offer a clean re-login.
          this._promptReauth()
          break
      }
    })
  }

  ngOnDestroy() {
    this.stop()
  }

  /** Start the background refresh loop. Idempotent - safe to call on every login. */
  start(): void {
    if (this._intervalId !== undefined) return

    this._keycloak.onTokenExpired = () => void this._refresh()
    this._intervalId = setInterval(() => void this._refresh(), REFRESH_INTERVAL_MS)
    void this._refresh()
  }

  /** Stop the loop and detach the Keycloak callback. */
  stop(): void {
    if (this._intervalId !== undefined) {
      clearInterval(this._intervalId)
      this._intervalId = undefined
    }
    this._keycloak.onTokenExpired = undefined
  }

  private async _refresh(): Promise<void> {
    if (this._refreshing || this._reauthDialog) return
    this._refreshing = true
    try {
      await this._keycloak.updateToken(TOKEN_MIN_VALIDITY_SECONDS)
    } catch (error) {
      console.warn('[TokenRefresh] silent token refresh failed', error)
      this._promptReauth()
    } finally {
      this._refreshing = false
    }
  }

  /**
   * The token can no longer be refreshed silently. The Keycloak SSO cookie is
   * often still valid, so `login()` round-trips through Keycloak and comes
   * straight back authenticated - no credentials, no logout page. Only if the
   * SSO session is also gone does the user see a login form.
   */
  private _promptReauth(): void {
    if (this._reauthDialog) return
    this.stop()

    this._reauthDialog = this._dialog.open(DialogConfirmComponent, {
      disableClose: true,
      width: '400px',
      data: {
        title: 'Session expired',
        message: 'Your session has timed out. Do you want to log back in?',
        cancelText: 'Logout',
        confirmText: 'Log back in',
      },
    })

    this._reauthDialog.afterClosed().subscribe((relogin: boolean) => {
      this._reauthDialog = undefined

      if (relogin) {
        this._keycloak.login({ redirectUri: reloginRedirectUri() })
      } else {
        this._keycloak.logout({ redirectUri: loggedOutRedirectUri() })
      }
    })
  }
}
